export const privacyPolicy = {
  slug: "privacy",
  title: "Privacy Policy",
  effectiveDate: "June 14, 2026",
  intro: [
    "BabyNext respects your privacy. This Privacy Policy explains how BabyNext handles information when you use the BabyNext mobile application, website, and related services.",
    "BabyNext is designed to help parents and caregivers track baby-related routines such as feeding, sleep, diaper changes, wake windows, and related care activities. BabyNext is intended for use by adults, not by children."
  ],
  sections: [
    {
      title: "1. Summary",
      body: [
        "BabyNext is built with a privacy-first approach.",
        "We do not store your private baby-tracking data on our servers. Information you enter into the app, such as baby names, schedules, feeding history, sleep history, diaper logs, notes, or similar care information, is stored locally on your device unless you choose to use device-level services such as backups or syncing provided by your operating system or device provider.",
        "We may collect limited anonymized or aggregated usage information to help us improve the app, understand performance, fix bugs, and improve the user experience. We do not use this data to identify you personally."
      ]
    },
    {
      title: "2. Information We Do Not Store on Our Servers",
      body: ["BabyNext does not store the following private information on our servers:"],
      list: [
        "Baby names",
        "Parent or caregiver names entered into the app",
        "Feeding, sleep, diaper, wake-window, or schedule history",
        "Notes or care-related entries",
        "Medical details or health observations",
        "Photos or personal media",
        "Private family information entered into the app"
      ],
      after: [
        "This information remains on your device unless your device, operating system, app store, backup provider, or other third-party service stores or syncs it outside of BabyNext's control."
      ]
    },
    {
      title: "3. Information Stored Locally on Your Device",
      body: [
        "The app may store information locally on your device so the app can function properly. This may include baby-tracking entries, preferences, reminders, settings, and app configuration data.",
        "You are responsible for maintaining access to your device and protecting it with appropriate security measures, such as a passcode, biometric lock, and secure device backups.",
        "If you delete the app, reset your device, disable backups, or lose access to your device, your locally stored information may be permanently lost. BabyNext is not responsible for lost, deleted, corrupted, or inaccessible local data."
      ]
    },
    {
      title: "4. Anonymized and Aggregated Data",
      body: ["We may collect anonymized, aggregated, or de-identified information about how the app is used. This may include information such as:"],
      list: [
        "App feature usage",
        "General performance metrics",
        "Crash or error reports",
        "Device type or operating system version",
        "General usage patterns",
        "Anonymous diagnostic information"
      ],
      after: [
        "We use this information to maintain, troubleshoot, improve, and develop BabyNext.",
        "We do not use anonymized or aggregated data to identify you, your baby, your household, or any specific individual. We do not attempt to re-identify anonymized data."
      ]
    },
    {
      title: "5. Analytics and Diagnostics",
      body: [
        "BabyNext may use analytics, crash reporting, or diagnostics tools to understand app performance and improve reliability. These tools are configured to avoid collecting private baby-tracking content.",
        "Analytics and diagnostic data may be processed by trusted service providers that help us operate and improve the app. These providers may process technical information on our behalf, subject to their own security and privacy practices.",
        "We do not sell your personal information. We do not use your private baby-tracking data for advertising. We do not share your private baby-tracking data with advertisers."
      ]
    },
    {
      title: "6. Device Permissions",
      body: [
        "BabyNext may request permission to use certain device features only when needed for app functionality. For example, BabyNext may request permission to send notifications or reminders.",
        "You can control device permissions through your device settings. If you disable certain permissions, some features of the app may not work as intended."
      ]
    },
    {
      title: "7. Support Communications",
      body: [
        "If you contact us for support, we may receive the information you choose to provide, such as your email address, message content, screenshots, diagnostic details, or other information included in your request.",
        "Please do not send sensitive personal, medical, or private baby-tracking information in support requests unless necessary. We use support communications only to respond to your request, troubleshoot issues, improve the app, and maintain appropriate business records."
      ]
    },
    {
      title: "8. How We Use Information",
      body: ["We may use information we collect or receive to:"],
      list: [
        "Provide and improve BabyNext",
        "Maintain app functionality",
        "Diagnose bugs, crashes, and performance issues",
        "Understand anonymized usage trends",
        "Respond to support requests",
        "Protect the app from abuse or misuse",
        "Comply with legal obligations"
      ]
    },
    {
      title: "9. How We Share Information",
      body: [
        "We do not sell your personal information.",
        "We do not share private baby-tracking data stored locally on your device because we do not store that data on our servers.",
        "We may share limited information in the following circumstances:"
      ],
      list: [
        "Service Providers: We may use trusted vendors for analytics, diagnostics, crash reporting, hosting, email, or support functions.",
        "Legal Compliance: We may disclose information if required by law, regulation, legal process, or governmental request.",
        "Protection of Rights: We may disclose information when necessary to protect BabyNext, users, or others from fraud, security threats, or misuse.",
        "Business Transfers: If BabyNext is involved in a merger, acquisition, financing, restructuring, or sale of assets, information may be transferred as part of that transaction, subject to this Privacy Policy."
      ]
    },
    {
      title: "10. Children's Privacy",
      body: [
        "BabyNext is intended for use by parents and caregivers who are adults. BabyNext is not directed to children under 13, and children under 13 may not use BabyNext.",
        "We do not knowingly collect personal information directly from children under 13. If we learn that we have collected personal information directly from a child under 13, we will take reasonable steps to delete it.",
        "Parents and caregivers are solely responsible for deciding what information to enter into the app about a child and for ensuring they have any necessary authority or consent to do so."
      ]
    },
    {
      title: "11. Health and Medical Information",
      body: [
        "BabyNext is a tracking and organization tool. BabyNext is not a medical device, does not provide medical advice, and should not be used to diagnose, treat, prevent, or manage any medical condition.",
        "You are solely responsible for how you use information entered into or displayed by BabyNext. Always consult a qualified medical professional regarding medical questions, symptoms, feeding concerns, sleep concerns, developmental concerns, or emergencies.",
        "Do not rely on BabyNext as a substitute for professional medical judgment or emergency care."
      ]
    },
    {
      title: "12. User Responsibility and Assumption of Risk",
      body: [
        "You are responsible for your use of BabyNext and for any decisions made based on information entered into or displayed by the app.",
        "BabyNext is provided as a convenience tool only. You are responsible for verifying the accuracy of entries, reminders, schedules, timers, and any other app output. You should not rely solely on BabyNext for childcare, medical, feeding, sleep, medication, safety, or emergency decisions.",
        "To the fullest extent permitted by law, BabyNext is not responsible for harm, loss, damages, missed reminders, inaccurate entries, device failures, data loss, user error, reliance on app information, or decisions made by you or any caregiver using the app."
      ]
    },
    {
      title: "13. Data Retention",
      body: [
        "Private baby-tracking data stored locally on your device remains on your device until you delete it, delete the app, reset your device, or otherwise remove it.",
        "Anonymized, aggregated, or de-identified analytics and diagnostic data may be retained for as long as reasonably necessary for app improvement, security, analytics, troubleshooting, and business purposes.",
        "Support communications may be retained for as long as reasonably necessary to respond to your request, improve BabyNext, resolve disputes, enforce agreements, or comply with legal obligations."
      ]
    },
    {
      title: "14. Security",
      body: [
        "We use reasonable technical and organizational measures designed to protect information we process. However, no app, device, network, or method of storage is completely secure.",
        "Because private baby-tracking data is stored locally on your device, you are responsible for protecting your device, account credentials, backups, and operating system settings."
      ]
    },
    {
      title: "15. Your Choices",
      body: ["You may be able to:"],
      list: [
        "Delete entries within the app",
        "Delete the app from your device",
        "Disable notifications or other permissions through device settings",
        "Opt out of certain analytics or diagnostics if the app provides that setting",
        "Contact us with privacy-related questions",
        "Request deletion of support communications you have sent to us, subject to legal and operational limitations"
      ]
    },
    {
      title: "16. State and International Privacy Rights",
      body: [
        "Depending on where you live, you may have privacy rights under applicable laws. These may include rights to access, correct, delete, or receive information about certain personal information.",
        "Because BabyNext does not store private baby-tracking data on our servers, we may not be able to access, export, correct, or delete information stored only on your device. You can manage locally stored data directly through the app or your device.",
        "To make a privacy request, contact us using the information below."
      ]
    },
    {
      title: "17. No Sale or Targeted Advertising",
      body: [
        "BabyNext does not sell your personal information. BabyNext does not use your private baby-tracking data for targeted advertising. BabyNext does not share your private baby-tracking data with data brokers or advertisers."
      ]
    },
    {
      title: "18. Third-Party Services",
      body: [
        "BabyNext may link to or interact with third-party services, platforms, app stores, operating systems, backup providers, analytics providers, or support tools. Their privacy practices are governed by their own privacy policies and terms.",
        "We are not responsible for the privacy, security, or data practices of third-party services that we do not control."
      ]
    },
    {
      title: "19. Changes to This Privacy Policy",
      body: [
        "We may update this Privacy Policy from time to time. If we make material changes, we may provide notice through the app, on our website, or by other reasonable means.",
        "The Effective Date above indicates when this Privacy Policy was last updated. Your continued use of BabyNext after an updated Privacy Policy becomes effective means you accept the updated policy."
      ]
    },
    {
      title: "20. Contact Us",
      body: ["If you have questions about this Privacy Policy or BabyNext's privacy practices, contact us at:"],
      contact: ["BabyNext", "Website: https://babynext.app", "Email: privacy@babynext.app"]
    }
  ]
};

export const termsOfService = {
  slug: "terms",
  title: "Terms of Service",
  effectiveDate: "June 14, 2026",
  intro: [
    "These Terms of Service govern your access to and use of BabyNext, including the BabyNext mobile application, website, software, features, content, and related services.",
    "By downloading, accessing, or using BabyNext, you agree to these Terms. If you do not agree, do not use BabyNext."
  ],
  sections: [
    {
      title: "1. About BabyNext",
      body: [
        "BabyNext is a baby routine tracking and planning tool designed to help parents and caregivers organize information related to baby care, such as feeding, sleeping, diaper changes, wake windows, reminders, and routine patterns.",
        "BabyNext is provided for informational, organizational, and convenience purposes only.",
        "BabyNext is not a medical device, healthcare provider, emergency service, clinical decision tool, diagnostic tool, treatment tool, or substitute for professional medical advice."
      ]
    },
    {
      title: "2. Eligibility",
      body: [
        "You must be at least 18 years old to use BabyNext.",
        "BabyNext is intended for use by parents, guardians, and caregivers who are legally permitted to make or assist with childcare decisions. BabyNext is not intended for use by children.",
        "By using BabyNext, you represent that you are at least 18 years old and have the authority to use the Service for the child or children whose information you enter into the app."
      ]
    },
    {
      title: "3. No Medical Advice",
      body: [
        "BabyNext does not provide medical advice, diagnosis, treatment, prescriptions, clinical recommendations, or emergency guidance.",
        "Any information, reminders, patterns, suggestions, summaries, timing estimates, or other outputs generated or displayed by BabyNext are for general informational and organizational purposes only.",
        "You should always consult a qualified healthcare provider regarding any medical questions, feeding concerns, sleep concerns, developmental concerns, symptoms, medications, illnesses, injuries, or emergencies.",
        "You should not disregard, delay, or avoid professional medical advice because of anything shown in BabyNext.",
        "In an emergency, contact emergency services immediately."
      ]
    },
    {
      title: "4. Parent and Healthcare Provider Responsibility",
      body: ["You, the parent, guardian, or caregiver, are solely responsible for all childcare decisions and actions.", "You acknowledge and agree that:"],
      list: [
        "You are responsible for verifying all information entered into BabyNext.",
        "You are responsible for evaluating any app-generated reminders, suggestions, schedules, or outputs.",
        "You are responsible for deciding whether and how to act on any information displayed by BabyNext.",
        "You are responsible for consulting your child's healthcare provider before making medical, feeding, sleep, medication, safety, or developmental decisions.",
        "BabyNext is not responsible for any action or inaction taken by you, another caregiver, a healthcare provider, or any other person based on use of the Service."
      ],
      after: ["BabyNext does not replace parental judgment, caregiver judgment, or professional healthcare judgment."]
    },
    {
      title: "5. No Responsibility for Suggested Actions",
      body: [
        "BabyNext may display reminders, patterns, estimates, prompts, suggested routines, or other suggested actions based on information entered into the app or general baby-care patterns.",
        "You understand and agree that these are not instructions, medical recommendations, or guarantees. They may be incomplete, inaccurate, delayed, inappropriate for your child, or based on incorrect or incomplete user-entered information.",
        "You are solely responsible for determining whether any suggested action is appropriate.",
        "To the fullest extent permitted by law, BabyNext disclaims responsibility for any harm, loss, injury, claim, liability, or damages arising from or related to any suggested action, reminder, schedule, estimate, notification, pattern, or app output."
      ]
    },
    {
      title: "6. No Health Information Stored on BabyNext Servers",
      body: [
        "BabyNext is designed so that private baby-tracking information is stored locally on your device, not on BabyNext servers.",
        "BabyNext does not intentionally collect, receive, maintain, or store personal health information, protected health information, medical records, clinical records, or individually identifiable health information on its servers.",
        "You should not use BabyNext to store emergency medical records, clinical instructions, medication orders, treatment plans, or other information that must be available for medical care.",
        "BabyNext may collect limited anonymized, aggregated, or diagnostic information as described in the BabyNext Privacy Policy.",
        "Your use of BabyNext is also governed by our Privacy Policy, available at: https://babynext.app/privacy"
      ]
    },
    {
      title: "7. Local Device Storage and Data Loss",
      body: [
        "Information you enter into BabyNext may be stored locally on your device. BabyNext is not responsible for backing up, restoring, preserving, or recovering locally stored information.",
        "You are solely responsible for maintaining your device, device backups, account access, operating system settings, and app data.",
        "BabyNext is not liable for lost, deleted, corrupted, inaccurate, inaccessible, or unrecoverable data, including data lost because of app deletion, device loss, device failure, operating system updates, user error, backup failure, sync failure, or other causes."
      ]
    },
    {
      title: "8. User Content",
      body: [
        "User Content means information you enter, create, store, or display through BabyNext, including baby routine data, notes, settings, reminders, and related information.",
        "You retain ownership of your User Content.",
        "You are solely responsible for your User Content, including its accuracy, legality, appropriateness, and use.",
        "You agree not to enter content that is unlawful, abusive, harmful, infringing, misleading, or that you do not have the right to use."
      ]
    },
    {
      title: "9. Acceptable Use",
      body: ["You agree not to:"],
      list: [
        "Use BabyNext for any unlawful purpose.",
        "Use BabyNext as a substitute for medical care.",
        "Use BabyNext in an emergency instead of contacting emergency services.",
        "Reverse engineer, decompile, modify, or attempt to extract the source code of the Service, except as permitted by law.",
        "Interfere with or disrupt the Service.",
        "Attempt to gain unauthorized access to BabyNext systems.",
        "Use BabyNext to violate another person's privacy or rights.",
        "Misrepresent BabyNext as a medical provider, healthcare service, emergency service, or clinical product."
      ]
    },
    {
      title: "10. App Updates and Changes",
      body: [
        "We may update, modify, suspend, discontinue, or remove features of BabyNext at any time.",
        "We do not guarantee that any feature will always be available or that the Service will be uninterrupted, error-free, secure, or compatible with every device or operating system version.",
        "You are responsible for installing updates when available. Some features may not function properly if you do not use the latest version of the app."
      ]
    },
    {
      title: "11. Third-Party Services",
      body: [
        "BabyNext may rely on or interact with third-party services, including app stores, operating systems, analytics providers, crash reporting tools, notification services, backup services, and payment processors.",
        "We do not control third-party services and are not responsible for their actions, omissions, availability, privacy practices, security practices, terms, or policies.",
        "Your use of third-party services may be subject to separate terms and privacy policies."
      ]
    },
    {
      title: "12. Purchases and Subscriptions",
      body: [
        "BabyNext may offer paid features, subscriptions, or in-app purchases.",
        "Purchases made through an app store are handled by the applicable app store provider. Billing, renewal, cancellation, refund, and payment terms may be governed by that provider's terms.",
        "Unless otherwise required by law or app store rules, payments are non-refundable.",
        "You are responsible for managing or canceling subscriptions through the platform where you purchased them."
      ]
    },
    {
      title: "13. Intellectual Property",
      body: [
        "BabyNext, including its software, design, interface, branding, trademarks, logos, content, and related materials, is owned by BabyNext or its licensors and is protected by intellectual property laws.",
        "Subject to your compliance with these Terms, BabyNext grants you a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to use the Service for personal, non-commercial childcare organization purposes.",
        "You may not copy, modify, distribute, sell, lease, sublicense, or create derivative works from BabyNext except as expressly permitted by these Terms or applicable law."
      ]
    },
    {
      title: "14. Disclaimer of Warranties",
      body: ["BabyNext is provided as is and as available.", "To the fullest extent permitted by law, BabyNext disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, reliability, availability, and uninterrupted operation.", "BabyNext does not warrant that:"],
      list: [
        "The Service will be accurate, reliable, complete, secure, uninterrupted, or error-free.",
        "Reminders or notifications will be delivered on time.",
        "Suggested actions or routines will be appropriate for your child.",
        "The Service will meet your expectations or needs.",
        "Data will always be preserved, backed up, or recoverable.",
        "Any errors or defects will be corrected."
      ],
      after: ["You use BabyNext at your own risk."]
    },
    {
      title: "15. Limitation of Liability",
      body: ["To the fullest extent permitted by law, BabyNext and its owners, officers, directors, employees, contractors, affiliates, licensors, and service providers will not be liable for any indirect, incidental, consequential, special, exemplary, punitive, or enhanced damages, including loss of data, loss of use, emotional distress, personal injury, childcare-related harm, medical harm, lost profits, or other damages arising from or related to your use of or inability to use BabyNext.", "To the fullest extent permitted by law, BabyNext is not liable for any damages arising from or related to:"],
      list: [
        "User-entered information",
        "Incorrect or incomplete data",
        "Suggested actions",
        "Missed, delayed, or inaccurate reminders",
        "Feeding, sleep, diaper, medication, safety, or medical decisions",
        "Reliance on app outputs",
        "Device failure",
        "Data loss",
        "Third-party services",
        "Unauthorized access to your device",
        "Actions or omissions of parents, caregivers, healthcare providers, or other users"
      ],
      after: [
        "To the fullest extent permitted by law, BabyNext's total liability for any claim arising from or related to the Service will not exceed the greater of: the amount you paid to BabyNext for the Service in the three months before the event giving rise to the claim, or $50.",
        "Some jurisdictions do not allow certain limitations of liability, so some limitations may not apply to you."
      ]
    },
    {
      title: "16. Indemnification",
      body: ["You agree to defend, indemnify, and hold harmless BabyNext and its owners, officers, directors, employees, contractors, affiliates, licensors, and service providers from and against any claims, damages, losses, liabilities, costs, and expenses, including reasonable attorneys' fees, arising from or related to:"],
      list: [
        "Your use or misuse of BabyNext",
        "Your User Content",
        "Your violation of these Terms",
        "Your violation of any law or third-party right",
        "Any childcare, medical, feeding, sleep, medication, safety, or developmental decision made by you or another caregiver",
        "Any action or inaction taken based on information displayed by BabyNext",
        "Your failure to consult a qualified healthcare provider when appropriate"
      ]
    },
    {
      title: "17. Termination",
      body: [
        "We may suspend or terminate your access to BabyNext at any time if we believe you have violated these Terms, created risk for BabyNext or others, or used the Service improperly.",
        "You may stop using BabyNext at any time.",
        "Sections that by their nature should survive termination will survive, including sections on no medical advice, user responsibility, disclaimers, limitation of liability, indemnification, intellectual property, governing law, and dispute resolution."
      ]
    },
    {
      title: "18. Governing Law",
      body: [
        "These Terms are governed by the laws of the State of Oklahoma, United States, without regard to conflict-of-law principles.",
        "If you operate BabyNext through a legal entity organized in another state or country, replace this section with the governing law applicable to that entity."
      ]
    },
    {
      title: "19. Dispute Resolution",
      body: [
        "Before filing a legal claim, you agree to first contact BabyNext at the email address below and attempt to resolve the dispute informally.",
        "If a dispute cannot be resolved informally, any legal action arising from or related to these Terms or BabyNext will be brought exclusively in the state or federal courts located in Oklahoma County, Oklahoma, unless applicable law requires otherwise.",
        "You consent to the personal jurisdiction and venue of those courts."
      ]
    },
    {
      title: "20. Changes to These Terms",
      body: [
        "We may update these Terms from time to time. If we make material changes, we may provide notice through the app, website, or other reasonable means.",
        "The Effective Date above shows when these Terms were last updated.",
        "Your continued use of BabyNext after updated Terms become effective means you accept the updated Terms."
      ]
    },
    {
      title: "21. Contact",
      body: ["Questions about these Terms may be sent to:"],
      contact: ["BabyNext", "Website: https://babynext.app", "Email: legal@babynext.app"]
    }
  ]
};
